import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RotateCcw, Search, ChevronUp, ChevronDown, ArrowUpDown, RefreshCw, CheckCircle, AlertCircle, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_COLORS = {
  OpenAI: '#10b981', Anthropic: '#f59e0b', Google: '#3b82f6',
  Meta: '#8b5cf6', xAI: '#ef4444', DeepSeek: '#06b6d4',
  Cohere: '#f97316', Kimi: '#ec4899',
};

const PROVIDER_PRICING_URLS = {
  OpenAI:    'https://openai.com/api/pricing/',
  Anthropic: 'https://www.anthropic.com/pricing',
  Google:    'https://ai.google.dev/pricing',
  DeepSeek:  'https://api-docs.deepseek.com/quick_start/pricing',
  xAI:       'https://docs.x.ai/docs/models',
  Cohere:    'https://cohere.com/pricing',
  Meta:      null,
  Kimi:      'https://platform.moonshot.cn/docs/pricing/text-generation',
};

const EMPTY_FORM = { provider: '', model: '', context: '', input_per_1k: '', output_per_1k: '' };

export default function LLMPricingAdmin({ token }) {
  const [models, setModels]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterProvider, setFilter] = useState('');
  const [sortField, setSortField]   = useState('provider');
  const [sortDir, setSortDir]       = useState('asc');
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);   // null = add, obj = edit
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [confirmSeed, setConfirmSeed] = useState(false);
  const [crawling, setCrawling]       = useState(false);
  const [crawlResults, setCrawlResults] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/llm-models`);
      setModels(res.data.models);
    } catch { toast.error('Failed to load models'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      provider: m.provider, model: m.model, context: m.context,
      input_per_1k: m.input_per_1k, output_per_1k: m.output_per_1k,
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.provider.trim() || !form.model.trim() || !form.context.trim()) {
      toast.error('Provider, model name, and context are required');
      return;
    }
    const payload = {
      ...form,
      input_per_1k:  parseFloat(form.input_per_1k)  || 0,
      output_per_1k: parseFloat(form.output_per_1k) || 0,
    };
    setSaving(true);
    try {
      if (editing) {
        const res = await axios.put(`${API}/admin/llm-models/${editing.id}`, payload, { headers });
        setModels(prev => prev.map(m => m.id === editing.id ? res.data : m));
        toast.success('Model updated');
      } else {
        const res = await axios.post(`${API}/admin/llm-models`, payload, { headers });
        setModels(prev => [...prev, res.data]);
        toast.success('Model added');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete "${m.model}" by ${m.provider}?`)) return;
    try {
      await axios.delete(`${API}/admin/llm-models/${m.id}`, { headers });
      setModels(prev => prev.filter(x => x.id !== m.id));
      toast.success('Model deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleReseed = async () => {
    setConfirmSeed(false);
    try {
      const res = await axios.post(`${API}/admin/llm-models/seed`, {}, { headers });
      toast.success(`Restored ${res.data.seeded} default models`);
      load();
    } catch { toast.error('Restore failed'); }
  };

  const handleCrawl = async () => {
    setCrawling(true);
    toast.info('Crawling pricing pages — this may take 30–60 seconds…');
    try {
      const res = await axios.post(`${API}/admin/llm-models/crawl-pricing`, {}, {
        headers,
        timeout: 120000,
      });
      setCrawlResults(res.data);
      toast.success(res.data.summary);
      load(); // refresh table
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Crawl failed');
    }
    setCrawling(false);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const providers = [...new Set(models.map(m => m.provider))].sort();

  const filtered = useMemo(() => {
    let r = models.filter(m => {
      const q = search.toLowerCase();
      const matchSearch = !q || m.model.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q);
      const matchProvider = !filterProvider || m.provider === filterProvider;
      return matchSearch && matchProvider;
    });
    r = [...r].sort((a, b) => {
      const mult = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'provider') return mult * a.provider.localeCompare(b.provider);
      if (sortField === 'model')    return mult * a.model.localeCompare(b.model);
      if (sortField === 'context')  return mult * a.context.localeCompare(b.context);
      return mult * (a[sortField] - b[sortField]);
    });
    return r;
  }, [models, search, filterProvider, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-25" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />;
  };

  return (
    <div className="space-y-4" data-testid="llm-pricing-admin">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search models..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 rounded-full text-sm"
            data-testid="llm-search"
          />
        </div>
        <select
          value={filterProvider}
          onChange={e => setFilter(e.target.value)}
          className="text-sm border border-border rounded-full px-3 h-9 bg-background text-foreground"
          data-testid="llm-filter-provider"
        >
          <option value="">All Providers</option>
          {providers.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5 text-xs border-primary/40 text-primary hover:bg-primary/10"
            onClick={handleCrawl}
            disabled={crawling}
            data-testid="refresh-pricing-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${crawling ? 'animate-spin' : ''}`} />
            {crawling ? 'Crawling…' : 'Refresh Pricing'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5 text-xs border-amber-500/40 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
            onClick={() => setConfirmSeed(true)}
            data-testid="restore-defaults-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restore Defaults
          </Button>
          <Button
            size="sm"
            className="rounded-full gap-1.5 text-xs shadow-lg shadow-primary/20"
            onClick={openAdd}
            data-testid="add-model-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add Model
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {filtered.length} of {models.length} models — changes reflect immediately on the{' '}
        <a href="/LLM-Cost-Calculator" className="text-primary hover:underline" target="_blank" rel="noreferrer">LLM Cost Calculator</a>
      </p>

      {/* Table */}
      <div className="border border-border/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/20">
                {[
                  { key: 'provider',      label: 'Provider' },
                  { key: 'model',         label: 'Model' },
                  { key: 'context',       label: 'Context' },
                  { key: 'input_per_1k',  label: 'Input / 1M' },
                  { key: 'output_per_1k', label: 'Output / 1M' },
                  { key: 'source',        label: 'Source', noSort: true },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => !col.noSort && handleSort(col.key)}
                    className={`px-4 py-2.5 text-left text-xs font-medium text-muted-foreground select-none whitespace-nowrap ${!col.noSort ? 'cursor-pointer hover:text-foreground' : ''}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.label} <SortIcon field={col.key} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-border/20">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted/50 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No models found
                  </td>
                </tr>
              ) : filtered.map(m => (
                <tr
                  key={m.id}
                  className="border-b border-border/20 hover:bg-accent/30 transition-colors group"
                  data-testid={`llm-row-${m.id}`}
                >
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        background: `${PROVIDER_COLORS[m.provider] || '#10b981'}22`,
                        color: PROVIDER_COLORS[m.provider] || '#10b981',
                      }}
                    >
                      {m.provider}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{m.model}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{m.context}</td>
                  <td className="px-4 py-3 font-mono text-xs">${(m.input_per_1k * 1000).toFixed(4)}</td>
                  <td className="px-4 py-3 font-mono text-xs">${(m.output_per_1k * 1000).toFixed(4)}</td>
                  <td className="px-4 py-3">
                    {PROVIDER_PRICING_URLS[m.provider] ? (
                      <a
                        href={PROVIDER_PRICING_URLS[m.provider]}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        title={PROVIDER_PRICING_URLS[m.provider]}
                        onClick={e => e.stopPropagation()}
                      >
                        <Globe className="w-3 h-3 shrink-0" />
                        <span className="truncate max-w-[140px]">{new URL(PROVIDER_PRICING_URLS[m.provider]).hostname.replace('www.', '')}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 rounded-md"
                        onClick={() => openEdit(m)}
                        data-testid={`edit-model-${m.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 rounded-md text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(m)}
                        data-testid={`delete-model-${m.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="llm-model-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Model Pricing' : 'Add New Model'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Provider</Label>
                <Input
                  placeholder="e.g. OpenAI"
                  value={form.provider}
                  onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
                  required
                  className="h-9"
                  data-testid="form-provider"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Context Window</Label>
                <Input
                  placeholder="e.g. 128k"
                  value={form.context}
                  onChange={e => setForm(f => ({ ...f, context: e.target.value }))}
                  required
                  className="h-9"
                  data-testid="form-context"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Model Name</Label>
              <Input
                placeholder="e.g. GPT-4.1 mini"
                value={form.model}
                onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                required
                className="h-9"
                data-testid="form-model"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Input price / 1k tokens ($)</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0.002"
                  value={form.input_per_1k}
                  onChange={e => setForm(f => ({ ...f, input_per_1k: e.target.value }))}
                  required
                  className="h-9 font-mono text-sm"
                  data-testid="form-input-price"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Output price / 1k tokens ($)</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0.008"
                  value={form.output_per_1k}
                  onChange={e => setForm(f => ({ ...f, output_per_1k: e.target.value }))}
                  required
                  className="h-9 font-mono text-sm"
                  data-testid="form-output-price"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Enter prices per 1,000 tokens (standard API unit). E.g. $1.25/1M = $0.00125 per 1k.
            </p>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 rounded-full shadow-lg shadow-primary/20" disabled={saving} data-testid="save-model-btn">
                {saving ? 'Saving…' : editing ? 'Update Model' : 'Add Model'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Restore Defaults Confirm */}
      <Dialog open={confirmSeed} onOpenChange={setConfirmSeed}>
        <DialogContent className="max-w-sm" data-testid="confirm-seed-modal">
          <DialogHeader>
            <DialogTitle>Restore Default Models?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-1">
            This will <strong>delete all current models</strong> and replace them with the latest built-in defaults.
            Any custom models or manual price edits will be lost.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setConfirmSeed(false)}>
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleReseed}
              data-testid="confirm-restore-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Yes, Restore
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crawl Results Modal */}
      <Dialog open={!!crawlResults} onOpenChange={() => setCrawlResults(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="crawl-results-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" /> Pricing Refresh Results
            </DialogTitle>
          </DialogHeader>
          {crawlResults && (
            <div className="space-y-4 mt-2">
              <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-3">
                <p className="text-sm font-semibold text-primary">{crawlResults.summary}</p>
              </div>
              <div className="space-y-3">
                {crawlResults.results.map(r => (
                  <div key={r.provider} className="border border-border/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {r.updated > 0
                          ? <CheckCircle className="w-4 h-4 text-primary" />
                          : <AlertCircle className="w-4 h-4 text-muted-foreground" />}
                        <span className="font-semibold text-sm">{r.provider}</span>
                        <span className="text-xs text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5">
                          {r.source === 'scraped' ? 'Web scraped' : 'AI knowledge'}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">
                        {r.updated} updated · {r.skipped} skipped
                      </span>
                    </div>
                    {r.scraped_url && (
                      <p className="text-xs text-muted-foreground mb-2 truncate">
                        Source: <a href={r.scraped_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{r.scraped_url}</a>
                      </p>
                    )}
                    {r.changes.length > 0 && (
                      <div className="space-y-1">
                        {r.changes.map(c => (
                          <div key={c.model} className="grid grid-cols-3 text-xs gap-2 py-1 border-t border-border/20">
                            <span className="font-medium truncate">{c.model}</span>
                            <span className="font-mono text-muted-foreground">
                              In: <span className={c.new_input < c.old_input ? 'text-primary' : c.new_input > c.old_input ? 'text-destructive' : ''}>
                                ${c.new_input.toFixed(5)}
                              </span>
                              {c.new_input !== c.old_input && <span className="text-muted-foreground/60"> (was ${c.old_input.toFixed(5)})</span>}
                            </span>
                            <span className="font-mono text-muted-foreground">
                              Out: <span className={c.new_output < c.old_output ? 'text-primary' : c.new_output > c.old_output ? 'text-destructive' : ''}>
                                ${c.new_output.toFixed(5)}
                              </span>
                              {c.new_output !== c.old_output && <span className="text-muted-foreground/60"> (was ${c.old_output.toFixed(5)})</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.updated === 0 && (
                      <p className="text-xs text-muted-foreground italic">No changes detected</p>
                    )}
                    {r.suspicious?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Flagged for manual review ({r.suspicious.length}):</p>
                        {r.suspicious.map(s => (
                          <div key={s.model} className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 rounded px-2 py-1">
                            <span className="font-medium text-foreground">{s.model}</span> — {s.reason}
                            <span className="ml-2 font-mono">
                              (proposed: ${s.proposed_input}/1M in · ${s.proposed_output}/1M out)
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button className="w-full rounded-full" onClick={() => setCrawlResults(null)}>Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
